/* eslint-disable max-len */
import lodash, { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useForm, useFormContext, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';

// COMPONENTS
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import KogInputAutoCompleteCheckControlled from '@/main/components/shared/inputAutoCompleteCheck/inputAutoCompleteCheckControlled';
import LoadingComponent from '@/main/components/shared/loading/loadingComponent';

// SERVICES
import { manufacturerApi, modelApi, partCatalogApi, subModelApi, supplierInventoryApi, vehicleApi, yearApi } from '@/main/api';
import { KeyValue } from '@/main/models/common';
import { ManufacturerRequest, ModelRequest, SubModelRequest, SupplierInventoryRequest, SupplierInventoryVehiclesRequest, VehicleYearRequest } from '@/main/models/request';
import { IXnuupPartId, SupplierInventoryDataResponse, SupplierInventoryVehicleResponse, XnuupPartIdAutoCompleteResponse } from '@/main/models/response';
import { PartCatalogSuggestionResponse, VehiclesSuggestionResponse } from '@/main/models/response/partCatalogResponse';
import { StatusSupplierInventoryWarehouseEnum } from '@/main/services/enum/StatusSupplierInventoryWarehouseEnum';
import { getTranslateByKey, isNullUndefined, removeEmptyValues } from '@/main/services/utils';

// REDUX
import { resetReducerYears } from '@/main/store/vehicle/year';

import * as SP from '../styles';
import { IInventoryRegisterForm } from '../utilsInventoryRegisterScreen';
import GridSectionVehicles from './gridSectionVehicles/gridSectionVehicles';
import * as S from './styles';
import { PaginationEnum } from '@/main/services/enum/PaginationEnum';

export interface ISectionVehiclesState {
  idClient: string;
  guidVehicle: string;
  guidManufacturer: string;
  guidModel: string;
  guidSubmodel: string;
  guidYear: string;
  manufacturer: string;
  model: string;
  submodel: string;
  year: string;
  suggestion?: boolean;
}
interface ISectionVehiclesProps {
  vehicles: ISectionVehiclesState[];
  vehiclesAPI: ISectionVehiclesState[];
  guidSupplierInventory: string | null
  dataInventory: SupplierInventoryDataResponse | null;
  guidCountry?: string | null | number;
  setVehicles: React.Dispatch<React.SetStateAction<ISectionVehiclesState[]>>;
  setVehiclesAPI: React.Dispatch<React.SetStateAction<ISectionVehiclesState[]>>;
  setVehiclesDelete: React.Dispatch<React.SetStateAction<string[]>>;
  setSuggestionFields: React.Dispatch<React.SetStateAction<PartCatalogSuggestionResponse>>;
}
interface IYearGroupedWithouKeyValue {
  nameGroup: string; // guidManufacturer + guidModel + guidSubmodel
  guidGroup: string; // guidManufacturer + guidModel + guidSubmodel
}
interface IYearView extends KeyValue {
  groups: (IYearGroupedWithouKeyValue & { guidYear: string | number })[];
}

const INITIAL_FORM_STATE = {
  manufacturer: null as KeyValue | null,
  model: null as KeyValue | null,
  trim: [] as KeyValue[],
  year: [] as IYearView[],
};

type ISectionVehiclesForm = typeof INITIAL_FORM_STATE;

function SectionVehicles({
  vehicles,
  guidSupplierInventory,
  guidCountry,
  dataInventory,
  vehiclesAPI,
  setVehiclesAPI,
  setVehicles,
  setVehiclesDelete,
  setSuggestionFields
}: ISectionVehiclesProps) {
  const dispatch = useDispatch();

  //#region Form & Watchers

  const methodsInventoryRegisterScreen = useFormContext<IInventoryRegisterForm>();
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    reset,
  } = useForm<ISectionVehiclesForm>({
    mode: 'all',
    defaultValues: INITIAL_FORM_STATE
  });
  const watcherMake = useWatch({
    name: 'manufacturer',
    control,
  })
  const watcherModel = useWatch({
    name: 'model',
    control,
  })
  const watcherTrim = useWatch({
    name: 'trim',
    control,
  })
  const watcherYear = useWatch({
    name: 'year',
    control,
  })
  //#endregion

  // #region States
  const [vehicleManufacturers, setVehicleManufacturers] = useState<KeyValue[] | null>(null);
  const [isLoadingVehicleManufacturer, setIsLoadingVehicleManufacturer] = useState(false);
  const [vehicleModels, setVehicleModels] = useState<KeyValue[] | null>(null);
  const [isLoadingVehicleModel, setIsLoadingVehicleModel] = useState(false);
  const [subModels, setSubModels] = useState<KeyValue[] | null>(null);
  const [isLoadingSubModels, setIsLoadingSubModels] = useState(false);
  const [yearsView, setYearsView] = useState<IYearView[]>([]);
  const [isLoadingYears, setIsLoadingYears] = useState(false);
  const [isLoadingVehicle, setIsLoadingVehicle] = useState<boolean>(false);

  // #endregion

  //#region Core Functions
  const getSubModelInGroup = (nameGroup: string) => nameGroup.split('/').slice(2).join('/');

  function updateYears(dataYears: KeyValue[], guidGroup: string, nameGroup: string = guidGroup) {
    const newYears = dataYears.map((year) => ({
      ...year,
      key: year.value,
      groups: [{ guidGroup, nameGroup, guidYear: year.key }]
    }));

    const newYearsView = newYears.reduce((accumulator, year) => {
      const yearsAlreadyExists = accumulator.find((item) => item.key === year.key);

      if (yearsAlreadyExists) {
        const newGroups = [...yearsAlreadyExists.groups, ...year.groups]
        const findIndex = accumulator.findIndex((item) => item.key === year.key);
        const namesGroups: string = newGroups.map((group) => getSubModelInGroup(group.nameGroup)).join(', ');
        const valueFormat = `${year.value} (${namesGroups})`
        accumulator[findIndex] = {
          ...yearsAlreadyExists,
          value: valueFormat,
          groups: newGroups
        }
      } else {
        const namesGroups: string = year.groups.map((group) => getSubModelInGroup(group.nameGroup)).join(', ');
        const valueFormat = `${year.value} (${namesGroups})`
        accumulator.push({
          ...year,
          value: valueFormat,
        });
      }

      return accumulator;
    }, yearsView);

    setYearsView([...newYearsView].sort((a, b) => Number(b.key) - Number(a.key)));
  }
  function removeYears(guidGroup: string) {
    const newYearsView = yearsView.reduce((accumulator, year) => {
      const newGroups = year.groups.filter((group) => group.guidGroup !== guidGroup);
      if (newGroups.length > 0) {
        const findGroup = year.groups.find((group) => group.guidGroup === guidGroup);
        const nameSubModelToRemove = getSubModelInGroup(String(findGroup?.nameGroup));
        const removeValue = year.value.split(String(year.key))[1].replace('(', '').replace(')', '').split(',').filter((subModel) => subModel.trim() !== nameSubModelToRemove).join(', ');

        accumulator.push({
          ...year,
          value: `${year.key} (${removeValue})`,
          groups: newGroups
        });
      }

      return accumulator;
    }, [] as IYearView[]);
    setYearsView(newYearsView);
  }
  function clearYears(group: string | null = null) {
    if (group === null) {
      setValue('year', INITIAL_FORM_STATE.year)
      setYearsView([]);
      return;
    }

  }
  function compareSuggestedVehicles(vehicles: SupplierInventoryVehicleResponse[], suggestionsList: VehiclesSuggestionResponse[]) {
    const suggestions: any = [];
    suggestionsList.forEach((suggestion: any) => {
      const existe = vehicles.some((veiculo: any) => veiculo.guidVehicle === suggestion.guidVehicle);
      if (!existe) {
        const veiculoSugerido = {
          ...suggestion,
          manufacturer: suggestion.vehicleManufacturer,
          model: suggestion.vehicleModel,
          year: suggestion.vehicleYear,
          submodel: suggestion.vehicleSubmodel,
          suggestion: true
        };
        suggestions.push(veiculoSugerido);
      }
    });
    const vehiclesGrid = [...vehicles, ...suggestions];
    return { suggestions, vehiclesGrid };
  }
  function checkSugestions(suggestionFields: PartCatalogSuggestionResponse) {
    if (suggestionFields.guidCategory && suggestionFields.category) {
      handleSetSuggestion('category', {
        key: suggestionFields.guidCategory || '',
        value: suggestionFields.category || ''
      });

      // callSubcategoryApiDomain({
      //   key: suggestionFields.guidCategory || '',
      //   value: suggestionFields.category || ''
      // });
    }

    if (suggestionFields.guidSubCategory && suggestionFields.subCategory) {
      handleSetSuggestion('subcategory', {
        key: suggestionFields.guidSubCategory || '',
        value: suggestionFields.subCategory || ''
      });
    }

    if (
      suggestionFields.partPIESName &&
      suggestionFields.guidPart &&
      suggestionFields.xnuupId
    ) {
      handleSetSuggestion('xnuupPartId', {
        partPIESName: suggestionFields.partPIESName || '',
        partGuid: suggestionFields.guidPart || '',
        piesPartTerminologyId: suggestionFields.xnuupId || 0
      });
    }

    if (suggestionFields.guidPosition && suggestionFields.position) {
      handleSetSuggestion('position', {
        key: suggestionFields.guidPosition || '',
        value: suggestionFields.position || ''
      });
    }
  }
  function handleSetSuggestion(field: string, value: KeyValue | XnuupPartIdAutoCompleteResponse) {
    switch (field) {
      case "category": {
        if ('key' in value && value.key && value.value) {
          methodsInventoryRegisterScreen.clearErrors('category');
          methodsInventoryRegisterScreen.setValue('category', value);
        }

        break;
      }
      case "subcategory": {
        if ('key' in value && value.key && value.value) {
          methodsInventoryRegisterScreen.clearErrors('subCategory');
          methodsInventoryRegisterScreen.setValue('subCategory', value);
        }
        break;
      }
      case "xnuupPartId": {
        if ('partGuid' in value && value.piesPartTerminologyId != 0 && value.partGuid && value.partPIESName) {
          const valueXnuup: IXnuupPartId = {
            id: value.piesPartTerminologyId,
            key: value.partGuid,
            value: value.partPIESName,
          };
          methodsInventoryRegisterScreen.clearErrors('xnuupPartId');
          methodsInventoryRegisterScreen.resetField('xnuupPartId');
          methodsInventoryRegisterScreen.setValue('xnuupPartId', valueXnuup);
        };

        break;
      }
      case "position": {
        if ('key' in value && value.key && value.value) {
          methodsInventoryRegisterScreen.clearErrors('position');
          methodsInventoryRegisterScreen.setValue('position', value);
        }
        break;
      }
    }
  }
  //#endregion

  //#region Async Functions

  async function loadVehiclesInventory(guidSupplierInventoryRequest: string | null) {
    try {
      if (isNullUndefined(guidSupplierInventoryRequest)) return;
      setIsLoadingVehicle(true);
      const request: SupplierInventoryVehiclesRequest = {
        guidSupplierInventory: guidSupplierInventoryRequest,
        pageIndex: PaginationEnum.PageIndexIntial,
        pageSize: PaginationEnum.PageSize15
      };
      const resultData = vehiclesAPI.map(vehicle => ({
        ...vehicle,
        idClient: vehicle.guidVehicle,
      }));
      let suggestionVehiclesData: VehiclesSuggestionResponse[] | null = null;

      if (
        !isNullUndefined(dataInventory) &&
        dataInventory.idStatus === StatusSupplierInventoryWarehouseEnum.PendingToMap
        && dataInventory.partNumberOEM
      ) {
        partCatalogApi
          .getSuggestion(dataInventory.partNumberOEM)
          .then(res => {
            const resData = res.data.result;
            suggestionVehiclesData = resData.vehicles;
          })
          .catch()
          .finally()
      }

      if (!isNullUndefined(suggestionVehiclesData)) {
        const { vehiclesGrid } = compareSuggestedVehicles(resultData, suggestionVehiclesData);

        setVehiclesAPI(vehiclesGrid);
      } else {
        setVehiclesAPI(resultData);
      }

      return vehiclesAPI;

    } catch (error) {

    } finally {
      setIsLoadingVehicle(false);
    }
  }

  async function callManufacturerApiDomain() {
    setIsLoadingVehicleManufacturer(true);
    setValue('manufacturer', null);
    setValue('model', null);
    setValue('trim', INITIAL_FORM_STATE.trim);
    setValue('year', INITIAL_FORM_STATE.year);
    setVehicleManufacturers(null);
    setVehicleModels(null);
    setSubModels(null);

    if (!isNullUndefined(guidCountry) && guidCountry !== '') {
      const request: ManufacturerRequest = {
        guidCountry: String(guidCountry)
      };
      manufacturerApi
        .getManufacturerByGuidCountry(request)
        .then(response => {
          const { result } = response.data;
          if (result) {
            setVehicleManufacturers(result);
          } else {
            setVehicleManufacturers(null);
          }
        })
        .finally(() => {
          setIsLoadingVehicleManufacturer(false);
        });
    }
  }

  async function callModelApiDomain(optionSelected: KeyValue | null) {
    setValue('manufacturer', optionSelected);
    setValue('model', null);
    setValue('trim', INITIAL_FORM_STATE.trim);
    setValue('year', INITIAL_FORM_STATE.year);
    setVehicleModels(null);
    setSubModels(null);
    clearYears();
    dispatch(resetReducerYears());

    if (optionSelected !== null) {
      setIsLoadingVehicleModel(true);
      const request: ModelRequest = {
        guidManufacturer: String(optionSelected.key),
        guidCountry: String(guidCountry)
      };
      modelApi
        .getVehicleModelByGuidCountry(request)
        .then(response => {
          const { result } = response.data;
          if (result) {
            setVehicleModels(result);
          } else {
            setVehicleModels(null);
          }
        })
        .finally(() => {
          setIsLoadingVehicleModel(false);
        });
    }
  }

  async function callSubModelApiDomain(optionSelected: KeyValue | null) {
    setValue('model', optionSelected);
    setValue('trim', INITIAL_FORM_STATE.trim);
    setValue('year', INITIAL_FORM_STATE.year);
    setSubModels(null);
    clearYears();
    if (optionSelected !== null) {
      setIsLoadingSubModels(true);
      const request: SubModelRequest = {
        guidManufacturer: String(
          String(getValues().manufacturer?.key)
        ),
        guidModel: String(optionSelected.key),
        guidCountry: String(guidCountry)
      };
      subModelApi
        .getVehicleSubmodelByGuidCountry(request)
        .then(response => {
          const { result } = response.data;
          if (result) {
            setSubModels(result);
          } else {
            setSubModels(null);
          }
        })
        .finally(() => {
          setIsLoadingSubModels(false);
        });
    }
  }
  async function callYearApiDomain(subModelSelected: KeyValue | undefined) {
    setValue('year', INITIAL_FORM_STATE.year);

    if (!isNullUndefined(subModelSelected)) {
      setIsLoadingYears(true);
      const filter: VehicleYearRequest = removeEmptyValues({
        guidManufacturer: String(watcherMake?.key),
        guidModel: String(watcherModel?.key),
        guidSubModel: String(subModelSelected.key),
      });

      yearApi
        .getVehicleYearByManufacturerAndModel(filter)
        .then(response => {
          const nameGroup = `${watcherMake?.value}/${watcherModel?.value}/${subModelSelected?.value}`;
          const guidGroup = `${filter.guidManufacturer}/${filter.guidModel}/${filter.guidSubModel}`;
          updateYears(response.data.result, guidGroup, nameGroup);
        })
        .finally(() => {
          setIsLoadingYears(false);
        });
    }
  }
  //#endregion

  //#region Handle Functions
  function handleSubmitSectionVehicles(data: ISectionVehiclesForm) {
    const customData = lodash.cloneDeep(data);
    if (!Array.isArray(data.year)) {
      customData.year = [data.year];
    }
    const makeGuidGroup = (guidSubModel: string) => `${customData.manufacturer?.key}/${customData.model?.key}/${guidSubModel}`
    const createAllVehicles = customData.trim.flatMap(trim =>
      customData.year.map(year => {
        const nameVehicle = `${customData.manufacturer?.value} ${customData.model?.value} ${trim.value} ${year.key}`
        const groupGuid = makeGuidGroup(String(trim.key));
        const findGroup = year.groups.find((group) => group.guidGroup === groupGuid);
        if (!findGroup) {
          const messageVehicleNotExists = getTranslateByKey('information.vehicleNotExists', true).replace('{0}', nameVehicle);
          toast.error(messageVehicleNotExists);
          return false;
        }
        return ({
          idClient: uuidv4(),
          guidVehicle: '',
          guidManufacturer: String(customData.manufacturer?.key),
          guidModel: String(customData.model?.key),
          guidSubmodel: String(trim.key),
          guidYear: String(findGroup.guidYear),
          manufacturer: String(customData.manufacturer?.value),
          model: String(customData.model?.value),
          submodel: String(trim.value),
          year: String(year.key),
        })


      })
    ).filter(Boolean) as ISectionVehiclesState[];

    const newVehicles = createAllVehicles.filter(vehicle => {
      const vehicleExists = vehicles.find((item) =>
        item.guidManufacturer === vehicle.guidManufacturer &&
        item.guidModel === vehicle.guidModel &&
        item.guidSubmodel === vehicle.guidSubmodel &&
        item.guidYear === vehicle.guidYear
      );
      if (vehicleExists) {
        const nameVehicle = `${vehicleExists.manufacturer} ${vehicleExists.model} ${vehicleExists.submodel} ${vehicleExists.year}`;
        toast.error(getTranslateByKey('information.DuplicatedVehicle', true).replace('{0}', nameVehicle));
      }
      return !vehicleExists;
    });

    if (newVehicles.length > 0) {
      setVehicles(prevState => [...prevState, ...newVehicles]);
      reset(INITIAL_FORM_STATE);
      setYearsView([]);
      setSubModels(null);
      setVehicleModels(null);
    }
  }

  function handleDeleteVehicleInfo(idClient: string) {
    const findedVehicle = vehicles.find(vehicle => vehicle.idClient === idClient);
    const findedVehicleAPI = vehiclesAPI.find(vehicle => vehicle.idClient === idClient);
    if (findedVehicle) {
      setVehicles(vehicles.filter(vehicle => vehicle.idClient !== idClient));
    } else if (findedVehicleAPI) {
      setVehiclesAPI(vehiclesAPI.filter(vehicle => vehicle.idClient !== idClient));
      setVehiclesDelete(prevState => [...prevState, findedVehicleAPI.guidVehicle]);
    }

  }
  //#endregion

  //#region methods Form
  const isDisabledManufacturerDrop = () => vehicleManufacturers === null;
  const isDisabledModelDrop = () => vehicleModels === null || vehicleManufacturers === null
  const isDisabledSubModelDrop = () => subModels === null || vehicleModels === null || vehicleManufacturers === null
  const isDisabledYearDrop = () => subModels === null || vehicleModels === null || vehicleManufacturers === null || yearsView.length === 0

  //#endregion

  //#region UseEffects
  useEffect(() => {
    if (!isEmpty(guidCountry)) {
      callManufacturerApiDomain();
    }
  }, [guidCountry]);

  useEffect(() => {
    loadVehiclesInventory(guidSupplierInventory);
  }, [JSON.stringify(vehiclesAPI), JSON.stringify(dataInventory)]);
  //#endregion

  return (
    <S.Container>
      <form
        onSubmit={handleSubmit(handleSubmitSectionVehicles)}
        noValidate>
        <SP.HeaderSection>
          <SP.HeaderText>
            {getTranslateByKey('information.vehicles')}
          </SP.HeaderText>
        </SP.HeaderSection>
        <S.AreaForm>
          <KogAutoCompleteControlled
            id="id-createOrder-autocomplete-manufacturer"
            nameCustom="manufacturer"
            label={getTranslateByKey('information.manufacturer')}
            control={control}
            options={vehicleManufacturers}
            isLoading={isLoadingVehicleManufacturer}
            rulesCustom={{ required: true }}
            hasErrorCustom={!!errors.manufacturer}
            errorMessageCustom={
              errors.manufacturer?.type === 'required'
                ? getTranslateByKey('information.manufacturerRequired', true)
                : ''
            }
            onChangeCustom={(optionSelected: KeyValue | null) => {
              callModelApiDomain(optionSelected);
            }}
            setValueCustom={setValue}
            disabled={isDisabledManufacturerDrop()}
          />
          <KogAutoCompleteControlled
            id="id-createOrder-autocomplete-model"
            nameCustom="model"
            label={getTranslateByKey('information.model')}
            control={control}
            options={vehicleModels}
            isLoading={isLoadingVehicleModel}
            rulesCustom={{ required: true }}
            hasErrorCustom={!!errors.model}
            errorMessageCustom={
              errors.model?.type === 'required'
                ? getTranslateByKey('information.modelRequired', true)
                : ''
            }
            onChangeCustom={(optionSelected: KeyValue | null) => {
              callSubModelApiDomain(optionSelected);
            }}
            setValueCustom={setValue}
            disabled={isDisabledModelDrop()}
          />

          <KogInputAutoCompleteCheckControlled
            name="trim"
            disableSelectAll
            control={control}
            options={subModels || []}
            label={getTranslateByKey('information.trim')}
            disabled={isDisabledSubModelDrop()}
            disabledOptions={watcherYear.length > 1 && watcherTrim.length > 0}
            isLoading={isLoadingSubModels}
            isFetching={isLoadingYears}
            controllerProps={{ rules: { required: true } }}
            customInputProps={{ required: true, id: 'id-createOrder-autocomplete-trim' }}
            errorMessageCustom={
              errors.trim?.type === 'required'
                ? getTranslateByKey('information.trimRequired', true)
                : ''
            }
            onChangeCustom={(value, optionChanged, reason) => {
              switch (reason) {
                case 'selectOption':
                  callYearApiDomain(optionChanged)
                  break;
                case 'removeOption':
                  const groupGuid = `${watcherMake?.key}/${watcherModel?.key}/${optionChanged?.key}`;
                  setValue('year', INITIAL_FORM_STATE.year);
                  removeYears(groupGuid);
                  break;
                case 'clear':
                  clearYears();
                  break;
                default:
                  break;
              }

            }}
          />
          {watcherTrim?.length > 1 ?
            <KogAutoCompleteControlled
              id="id-createOrder-autocomplete-year"
              nameCustom="year"
              label={getTranslateByKey('information.year')}
              hideValue={watcherYear.length === 0}
              control={control}
              options={yearsView}
              isLoading={isLoadingYears}
              rulesCustom={{ required: true }}
              hasErrorCustom={!!errors.year}
              errorMessageCustom={
                errors.year?.type === 'required'
                  ? getTranslateByKey('information.yearRequired', true)
                  : ''
              }
              onChangeCustom={(optionSelected: KeyValue | null) => {
                const valueCustom = isNullUndefined(optionSelected) ? INITIAL_FORM_STATE.year : optionSelected;
                setValue('year', valueCustom as IYearView[]);
              }}
              setValueCustom={setValue}
              disabled={isDisabledYearDrop()}
            /> :
            <KogInputAutoCompleteCheckControlled
              name="year"
              disableSelectAll
              control={control}
              options={yearsView}
              label={getTranslateByKey('information.year')}
              disabled={isDisabledYearDrop()}
              disabledOptions={watcherTrim.length > 1 && watcherYear.length > 0}
              isLoading={isLoadingYears}
              isFetching={isLoadingSubModels}
              controllerProps={{ rules: { required: true } }}
              customInputProps={{ required: true, id: 'id-createOrder-autocomplete-year' }}
              errorMessageCustom={
                errors.year?.type === 'required'
                  ? getTranslateByKey('information.yearRequired', true)
                  : ''
              }
            />
          }

        </S.AreaForm>
        <S.ContainerButton>
          <SecondaryButton
            type="submit"
            style={{ padding: '.2rem .8rem', margin: '.5rem 0 0 0' }}
            id="id-register-btn-addContact">
            {`${getTranslateByKey('information.add')} ${getTranslateByKey('information.vehicle').toLowerCase()}`}
          </SecondaryButton>
        </S.ContainerButton>
      </form>
      {isLoadingVehicle ? <LoadingComponent /> :
        <>
          <SP.InfoSection>
            {vehicles.length > 1 ? (
              <SP.InfoText>
                {`${getTranslateByKey('information.partCompatible')} ${vehicles.length}
              ${getTranslateByKey('information.vehicles').toLowerCase()}`}
              </SP.InfoText>) : vehicles.length === 1 ? (
                <SP.InfoText>
                  {`${getTranslateByKey('information.partCompatible')} ${vehicles.length}
            ${getTranslateByKey('information.vehicle').toLowerCase()}`}
                </SP.InfoText>) : null}
          </SP.InfoSection>
          <GridSectionVehicles
            vehicles={vehicles}
            vehiclesAPI={vehiclesAPI}
            setVehiclesAPI={setVehiclesAPI}
            request={isNullUndefined(guidSupplierInventory) ? null : {
              guidSupplierInventory: isNullUndefined(guidSupplierInventory) ? '' : String(guidSupplierInventory),
              pageIndex: PaginationEnum.PageIndexIntial,
              pageSize: PaginationEnum.PageSize15
            }}
            handleDeleteVehicleInfo={handleDeleteVehicleInfo}
          />
        </>
      }
    </S.Container >
  );
}

export default SectionVehicles;
